// src/pages/AwakenPage.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { QUIZ_STEPS, PERKS_LIST, computeDemon } from "../data/demons";
import DemonCard from "../components/DemonCard";
import "./AwakenPage.css";

export default function AwakenPage() {
  const { user, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const [step, setStep]           = useState(0); // 0..QUIZ_STEPS.length = perks, +1 = result
  const [answers, setAnswers]     = useState({});
  const [perks, setPerks]         = useState([]);
  const [demon, setDemon]         = useState(null);
  const [saving, setSaving]       = useState(false);

  const totalSteps = QUIZ_STEPS.length + 1; // +1 for perks
  const progress   = Math.round((step / totalSteps) * 100);
  const isPerksStep = step === QUIZ_STEPS.length;
  const isResult    = step === totalSteps;

  const currentQ = !isPerksStep && !isResult ? QUIZ_STEPS[step] : null;

  const selectAnswer = (value) => {
    setAnswers(prev => ({ ...prev, [currentQ.id]: value }));
  };

  const togglePerk = (id) => {
    setPerks(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  const next = () => {
    if (isPerksStep) {
      if (perks.length < 3) { alert("Sélectionne au moins 3 perks."); return; }
      const result = computeDemon(answers, perks);
      setDemon(result);
      setStep(s => s + 1);
    } else {
      setStep(s => s + 1);
    }
  };

  const prev = () => setStep(s => Math.max(0, s - 1));

  const canNext = isPerksStep ? perks.length >= 3 : !!answers[currentQ?.id];

  const saveDemon = async () => {
    if (!user) { navigate("/auth?redirect=/awaken"); return; }
    setSaving(true);
    try {
      await setDoc(doc(db, "users", user.uid), {
        demon: { ...demon, savedAt: serverTimestamp() }
      }, { merge: true });
      await refreshProfile();
      navigate("/profile");
    } catch (e) {
      console.error(e);
      setSaving(false);
    }
  };

  const restart = () => {
    setStep(0); setAnswers({}); setPerks([]); setDemon(null);
  };

  return (
    <div className="awaken-page">
      <div className="awaken-bg" aria-hidden="true" />

      <div className="section" style={{ paddingTop: "7rem" }}>
        <div className="awaken-header">
          <span className="section-eyebrow">覚醒 · Le Réveil</span>
          <h1 className="section-title">Trouve ton Démon</h1>
          <div className="divider divider-center" />
          {!isResult && (
            <p className="awaken-sub">Réponds honnêtement. Ton démon te connaît déjà mieux que toi.</p>
          )}
        </div>

        {/* Progress */}
        {!isResult && (
          <div className="quiz-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: progress + "%" }} />
            </div>
            <span className="progress-label">{step} / {totalSteps}</span>
          </div>
        )}

        {/* Quiz Steps */}
        {!isResult && (
          <div className="quiz-card">
            {currentQ && (
              <div className="quiz-step">
                <h2 className="quiz-question">{currentQ.question}</h2>
                <p className="quiz-sub">{currentQ.subtitle}</p>
                <div className="quiz-options">
                  {currentQ.options.map(opt => (
                    <button
                      key={opt.value}
                      className={`quiz-option${answers[currentQ.id] === opt.value ? " selected" : ""}`}
                      onClick={() => selectAnswer(opt.value)}
                    >
                      <span className="opt-icon">{opt.icon}</span>
                      <div className="opt-text">
                        <strong>{opt.label}</strong>
                        <span>{opt.desc}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isPerksStep && (
              <div className="quiz-step">
                <h2 className="quiz-question">Tes perks démoniaques.</h2>
                <p className="quiz-sub">Sélectionne tous ceux qui te définissent. Honnêteté totale. ({perks.length} sélectionné{perks.length > 1 ? "s" : ""}, 3 minimum)</p>
                <div className="perks-select-grid">
                  {PERKS_LIST.map(p => (
                    <button
                      key={p.id}
                      className={`perk-select-btn${perks.includes(p.id) ? " active" : ""}`}
                      onClick={() => togglePerk(p.id)}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="quiz-nav">
              {step > 0 && (
                <button className="btn btn-ghost btn-sm" onClick={prev}>← RETOUR</button>
              )}
              <button
                className="btn btn-primary btn-sm"
                onClick={next}
                disabled={!canNext}
              >
                {isPerksStep ? "RÉVÉLER MON DÉMON 🔥" : "SUIVANT →"}
              </button>
            </div>
          </div>
        )}

        {/* Result */}
        {isResult && demon && (
          <div className="result-area">
            <div className="result-reveal-text">
              <span className="section-eyebrow">Ton démon est apparu</span>
              <h2 className="section-title" style={{ color: "var(--blood)" }}>
                {demon.oni.jp}
              </h2>
            </div>

            <DemonCard demon={demon} />

            <div className="result-actions">
              {user ? (
                <button className="btn btn-primary" onClick={saveDemon} disabled={saving}>
                  {saving ? <><span className="spinner" /> SAUVEGARDE...</> : "SAUVEGARDER MON DÉMON →"}
                </button>
              ) : (
                <button className="btn btn-primary" onClick={() => navigate("/auth?redirect=/awaken")}>
                  CRÉER UN COMPTE POUR SAUVEGARDER →
                </button>
              )}
              <button className="btn btn-ghost" onClick={restart}>RECOMMENCER ↺</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
